import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import ThemeMaker from "../../components/thememaker";
import { tokenDecode } from "../../utils/helper";
import { config } from "../../utils/config";
import Circularprogress from "../../components/Generics/Circularprogress";
import "./MasterCatalogue.css";

const { apiEndpoint, apiUrl, baseNodeapiUrl } = config;
const MCR_BASE = `${baseNodeapiUrl}/mcr-api`;
const FALLBACK_IMAGE = "https://via.placeholder.com/120?text=No+Image";
const LIMIT = 20;

const inr = (v) => Number(v || 0).toLocaleString("en-IN");

/* ────────────────────────────────────────────────────────────────────────── */

function McrTableRow({ item, selected, onToggle }) {
  const img = item?.images?.split(",")?.[0]?.trim() || FALLBACK_IMAGE;
  const selling = inr(item?.amazon_price || item?.brand_price || 0);
  const showMrp = item?.brand_price && item?.brand_price !== item?.amazon_price;
  const meta = [item?.brand, item?.category, item?.sub_category].filter(Boolean).join(" · ") || "—";
  return (
    <tr className={selected ? "is-selected" : ""} onClick={onToggle}>
      <td style={{ textAlign: "center" }}>
        <input type="checkbox" className="mcat-check" checked={selected} readOnly />
      </td>
      <td>
        <img className="mcat-thumb" src={img} alt="" />
      </td>
      <td>
        <div className="mcat-name">
          <span>{item?.product_name || "—"}</span>
          {item?.in_store ? <span className="mcat-instore">In Store</span> : null}
        </div>
        <div className="mcat-meta">{meta}</div>
      </td>
      <td className="mcat-price">
        <span className="mcat-price-now">₹{selling}</span>
        {showMrp ? <span className="mcat-price-mrp">₹{inr(item.brand_price)}</span> : null}
      </td>
    </tr>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

const MasterCatalogue = () => {
  const decoded = tokenDecode();
  const sellerId = decoded?.data?.user?.seller_id;
  const providerId = decoded?.data?.seller_data?.provider_id;

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [brand, setBrand] = useState("");

  // Selection + add-to-catalog
  const [selected, setSelected] = useState({}); // key -> item
  const [whModalOpen, setWhModalOpen] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [whLoading, setWhLoading] = useState(false);
  const [selectedWh, setSelectedWh] = useState({}); // whKey -> {warehouse_id, warehouse_location_id, ...}
  const [submitting, setSubmitting] = useState(false);

  const [filterData, setFilterData] = useState({
    categories: [],
    sub_categories: [],
    sub_categories_by_category: {},
    brands: [],
  });

  // ── Filter options ──────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${MCR_BASE}/products/filters`);
        setFilterData({
          categories: data?.categories || [],
          sub_categories: data?.sub_categories || [],
          sub_categories_by_category: data?.sub_categories_by_category || {},
          brands: data?.brands || [],
        });
      } catch (e) {
        console.log("MCR filters error →", e?.message);
      }
    })();
  }, []);

  const categories = useMemo(
    () =>
      (filterData.categories || [])
        .filter((x) => x?.category)
        .map((x) => ({ value: x.category, label: `${x.category} (${inr(x.count)})` })),
    [filterData.categories]
  );

  const subCategories = useMemo(() => {
    let items = [];
    if (category && filterData.sub_categories_by_category?.[category]) {
      items = filterData.sub_categories_by_category[category];
    } else {
      items = category
        ? filterData.sub_categories.filter((x) => x?.category === category)
        : filterData.sub_categories;
    }
    return (items || [])
      .filter((x) => x?.sub_category)
      .map((x) => ({ value: x.sub_category, label: `${x.sub_category} (${inr(x.count)})` }));
  }, [filterData, category]);

  const brands = useMemo(
    () =>
      (filterData.brands || [])
        .filter((x) => x?.brand)
        .map((x) => ({ value: x.brand, label: `${x.brand} (${inr(x.count)})` })),
    [filterData.brands]
  );

  // ── Debounced live search ───────────────────────────────────────────────
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  // ── Fetch listing (server-side filters + pagination) ────────────────────
  const fetchMcr = useCallback(
    async (pg = 1) => {
      setLoading(true);
      try {
        const url =
          `${MCR_BASE}/products?page=${pg}&limit=${LIMIT}` +
          (providerId ? `&provider_id=${encodeURIComponent(providerId)}` : "") +
          `&category=${encodeURIComponent(category)}` +
          `&sub_category=${encodeURIComponent(subCategory)}` +
          `&brand=${encodeURIComponent(brand)}` +
          `&search=${encodeURIComponent(search)}`;
        const { data } = await axios.get(url);
        setList(data?.data || []);
        setTotal(data?.total || 0);
        setTotalPages(data?.total_pages || 1);
      } catch (e) {
        console.log("MCR fetch error →", e?.response?.status, e?.message);
        setList([]);
      } finally {
        setLoading(false);
      }
    },
    [category, subCategory, brand, search, providerId]
  );

  useEffect(() => {
    fetchMcr(page);
  }, [page, fetchMcr]);

  const onCategoryChange = (v) => {
    setCategory(v);
    setSubCategory("");
    setBrand("");
    setPage(1);
  };
  const onSubCategoryChange = (v) => {
    setSubCategory(v);
    setBrand("");
    setPage(1);
  };
  const onBrandChange = (v) => {
    setBrand(v);
    setPage(1);
  };

  const hasFilters = category || subCategory || brand || search;
  const clearFilters = () => {
    setCategory("");
    setSubCategory("");
    setBrand("");
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  // ── Selection helpers ──────────────────────────────────────────────────
  const getUpi = (item) => item?.upi_id || item?.pid || item?.id;
  const selectedCount = Object.keys(selected).length;

  const toggleSelect = (item) => {
    const key = getUpi(item);
    if (!key) return;
    setSelected((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = item;
      return next;
    });
  };

  const whKey = (w) => `${String(w.warehouse_id)}|${String(w.warehouse_location_id)}`;
  const toggleWarehouse = (w) => {
    setSelectedWh((prev) => {
      const next = { ...prev };
      const k = whKey(w);
      if (next[k]) delete next[k];
      else next[k] = w;
      return next;
    });
  };

  // Warehouses already holding ALL currently selected products
  const alreadyAddedWhKeys = useMemo(() => {
    const items = Object.values(selected);
    if (items.length === 0) return new Set();
    const sets = items.map(
      (item) => new Set((Array.isArray(item?.warehouses) ? item.warehouses : []).map(whKey))
    );
    return sets.reduce((acc, s) => new Set([...acc].filter((k) => s.has(k))));
  }, [selected]);

  // ── Warehouse list ────────────────────────────────────────────────────
  const openCatalog = async () => {
    if (selectedCount === 0) return;
    setSelectedWh({});
    setWhModalOpen(true);
    setWhLoading(true);
    try {
      const response = await axios({
        url: `${apiEndpoint}/get-warehouse-list`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "X-Custom-Authorization": `Bearer ${sessionStorage.getItem("us_st_d")}`,
        },
      });
      setWarehouses(response?.data?.data || []);
    } catch (e) {
      console.log("store list error →", e?.response?.status, e?.message);
      setWarehouses([]);
    } finally {
      setWhLoading(false);
    }
  };

  // ── POST /mcr-api/products/add-to-catalog ─────────────────────────────
  const submitAddToCatalog = async () => {
    const whList = Object.values(selectedWh);
    if (whList.length === 0) {
      toast.error("Please select at least one store.");
      return;
    }
    if (!sellerId) {
      toast.error("Seller not identified. Please re-login.");
      return;
    }
    setSubmitting(true);
    try {
      const products = Object.keys(selected); // plain upi_id strings -> top-level defaults
      const payload = { seller_id: Number(sellerId), products };
      if (whList.length === 1) {
        payload.warehouse_id = Number(whList[0].warehouse_id);
        payload.warehouse_location_id = whList[0].warehouse_location_id;
      } else {
        payload.warehouses = whList.map((w) => ({
          warehouse_id: Number(w.warehouse_id),
          warehouse_location_id: w.warehouse_location_id,
        }));
      }

      const { data } = await axios.post(`${MCR_BASE}/products/add-to-catalog`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      const s = data?.summary;
      const results = data?.results || [];
      const failed = results.filter((r) => r?.status !== "added");
      let msg = s
        ? `Added ${s.added}/${s.total} products.` +
          (s.already_added ? ` Already added: ${s.already_added}.` : "") +
          (s.not_found ? ` Not found: ${s.not_found}.` : "") +
          (s.failed ? ` Failed: ${s.failed}.` : "")
        : data?.message || "Done";
      if (failed.length) {
        msg += ` Issues: ` + failed.map((r) => `${r.id}: ${r.status}`).join(", ");
      }
      toast.success(msg);
      setWhModalOpen(false);
      setSelected({});
      setSelectedWh({});
      fetchMcr(page);
    } catch (e) {
      const serverMsg = e?.response?.data?.message || e?.response?.data?.error;
      console.log("add-to-catalog error →", e?.response?.status, e?.response?.data);
      toast.error(serverMsg || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const whSelectedCount = Object.keys(selectedWh).length;

  /* ──────────────────────────────────────────────────────────────────── */

  return (
    <ThemeMaker>
      <div className="mcat-page" style={{ paddingBottom: selectedCount > 0 ? 100 : 24 }}>
        <div className="mcat-card">
          <div className="mcat-head">
            <div>
              <h3 className="mcat-head-title">Master Catalogue</h3>
              <p className="mcat-head-sub">Master Catalogue Repository &middot; add products to your catalogue</p>
            </div>
            <span className="mcat-head-count">
              {list.length} shown{total > 0 ? ` / ${inr(total)}` : ""}
            </span>
          </div>

          {/* Filters */}
          <div className="mcat-toolbar">
            <input
              type="search"
              className="mcat-input"
              placeholder="Search (e.g. samsung)…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <select className="mcat-select" value={category} onChange={(e) => onCategoryChange(e.target.value)}>
              <option value="">All Categories</option>
              {categories.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            <select className="mcat-select" value={subCategory} onChange={(e) => onSubCategoryChange(e.target.value)}>
              <option value="">All Sub-categories</option>
              {subCategories.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <select className="mcat-select" value={brand} onChange={(e) => onBrandChange(e.target.value)}>
              <option value="">All Brands</option>
              {brands.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
            {hasFilters ? (
              <button className="mcat-clear" onClick={clearFilters}>
                Clear
              </button>
            ) : (
              <span />
            )}
          </div>

          {/* List */}
          {loading ? (
            <div className="mcat-state">
              <Circularprogress />
            </div>
          ) : list.length === 0 ? (
            <div className="mcat-state">No MCR products found.</div>
          ) : (
            <div className="mcat-scroll">
              <table className="mcat-table">
                <thead>
                  <tr>
                    <th className="mcat-th-check"> </th>
                    <th className="mcat-th-img">Image</th>
                    <th>Product</th>
                    <th className="mcat-th-price">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((item) => (
                    <McrTableRow
                      key={String(getUpi(item))}
                      item={item}
                      selected={!!selected[getUpi(item)]}
                      onToggle={() => toggleSelect(item)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="mcat-pager">
              <button
                className="mcat-pager-btn"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
              >
                &lt; Prev
              </button>
              <span className="mcat-pager-info">
                Page {page} / {totalPages}
              </span>
              <button
                className="mcat-pager-btn"
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              >
                Next &gt;
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Selection action bar */}
      {selectedCount > 0 && (
        <div className="mcat-selbar">
          <span className="mcat-selbar-count">
            {selectedCount} selected
            <button className="mcat-selbar-clear" onClick={() => setSelected({})}>
              Clear
            </button>
          </span>
          <button className="mcat-selbar-add" onClick={openCatalog}>
            + Add to Catalogue
          </button>
        </div>
      )}

      {/* Warehouse selection modal */}
      {whModalOpen && (
        <div className="mcat-modal-overlay" onClick={() => setWhModalOpen(false)}>
          <div className="mcat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mcat-modal-head">
              <h6 className="mcat-modal-title">Select Store(s)</h6>
              <button className="mcat-modal-close" onClick={() => setWhModalOpen(false)}>
                &times;
              </button>
            </div>
            <div className="mcat-modal-body">
              <p className="mcat-modal-note">
                {selectedCount} product{selectedCount > 1 ? "s" : ""} → chosen store(s)
              </p>

              {whLoading ? (
                <div className="text-center py-4">
                  <Circularprogress />
                </div>
              ) : warehouses.length === 0 ? (
                <p className="text-center text-muted py-4">No stores found. Add one first.</p>
              ) : (
                <div className="mcat-wh-list">
                  {warehouses.map((w, i) => {
                    const wObj = { warehouse_id: w?.id, warehouse_location_id: w?.location_id };
                    const alreadyAdded = alreadyAddedWhKeys.has(whKey(wObj));
                    const on = alreadyAdded || !!selectedWh[whKey(wObj)];
                    return (
                      <div
                        key={String(w?.id || i)}
                        onClick={() => {
                          if (!alreadyAdded) toggleWarehouse(wObj);
                        }}
                        className={`mcat-wh-row ${on ? "is-on" : ""}`}
                        style={{ cursor: alreadyAdded ? "not-allowed" : "pointer", opacity: alreadyAdded ? 0.6 : 1 }}
                      >
                        <div>
                          <div className="mcat-wh-name">{w?.warehouse_name || "Store"}</div>
                          {w?.city ? <div className="mcat-wh-city">{w.city}</div> : null}
                          {alreadyAdded ? <div className="mcat-wh-added">Already added</div> : null}
                        </div>
                        <input type="checkbox" className="mcat-check" checked={on} readOnly />
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                className="mcat-modal-submit"
                onClick={submitAddToCatalog}
                disabled={submitting || whSelectedCount === 0}
              >
                {submitting
                  ? "Pushing…"
                  : `Push ${selectedCount} product${selectedCount > 1 ? "s" : ""}` +
                    (whSelectedCount > 1 ? ` to ${whSelectedCount} stores` : "")}
              </button>
            </div>
          </div>
        </div>
      )}
    </ThemeMaker>
  );
};

export default MasterCatalogue;
